import BaseApiService from "./BaseApiService";
import axios from "axios";
import UseCaseResponse from "@/types/UseCaseResponse";
import CanvasCourseMapping from "@/types/Canvas/CanvasCourseMapping";
import CanvasSyncSettings from "@/types/Canvas/CanvasSyncSettings";
import SyncResult from "@/types/Canvas/SyncResult";

// The global axios interceptor in App.vue resolves error responses (4xx/5xx) by
// returning the AxiosError object instead of re-throwing. Because of that, a
// caller doing `resp.data` on the result silently gets `undefined` on errors.
// JsonPresenter (backend) returns 400 with a structured UseCaseResponse body
// when use-case validation fails, so we want to surface that body to the form.
function unwrap<T>(resp: any): UseCaseResponse<T> {
  // Success path: AxiosResponse with .data
  if (
    resp &&
    resp.data &&
    typeof resp.data === "object" &&
    "Success" in resp.data
  ) {
    return resp.data as UseCaseResponse<T>;
  }
  // Error path: AxiosError with .response.data (likely from a 400 with a JsonPresenter body)
  if (
    resp &&
    resp.response &&
    resp.response.data &&
    typeof resp.response.data === "object" &&
    "Success" in resp.response.data
  ) {
    return resp.response.data as UseCaseResponse<T>;
  }
  // Network error or unrecognized shape — synthesize a failed response so callers
  // can rely on `.Success` always being defined.
  return {
    Value: (undefined as unknown) as T,
    Success: false,
    Message: (resp && resp.message) || "Network or server error",
    Errors: []
  } as UseCaseResponse<T>;
}

export default class CanvasCourseMappingApiService extends BaseApiService {
  async GetMappings(): Promise<UseCaseResponse<CanvasCourseMapping[]>> {
    await this.defineHeader();
    return axios
      .get(`${this.url}/api/canvas/CanvasCourseMapping`)
      .then(resp => unwrap<CanvasCourseMapping[]>(resp));
  }

  async GetMappingById(
    id: string
  ): Promise<UseCaseResponse<CanvasCourseMapping>> {
    await this.defineHeader();
    return axios
      .get(`${this.url}/api/canvas/CanvasCourseMapping/${id}`)
      .then(resp => unwrap<CanvasCourseMapping>(resp));
  }

  async CreateMapping(
    m: CanvasCourseMapping
  ): Promise<UseCaseResponse<string>> {
    await this.defineHeader();
    return axios
      .post(`${this.url}/api/canvas/CanvasCourseMapping`, m)
      .then(resp => unwrap<string>(resp));
  }

  async UpdateMapping(
    m: CanvasCourseMapping
  ): Promise<UseCaseResponse<boolean>> {
    await this.defineHeader();
    return axios
      .put(`${this.url}/api/canvas/CanvasCourseMapping/${m.Id}`, m)
      .then(resp => unwrap<boolean>(resp));
  }

  async DeleteMapping(id: string): Promise<UseCaseResponse<boolean>> {
    await this.defineHeader();
    return axios
      .delete(`${this.url}/api/canvas/CanvasCourseMapping/${id}`)
      .then(resp => unwrap<boolean>(resp));
  }

  // Operator-triggered run. Bypasses the cron's #if DEBUG guard, so works in local dev too.
  async SyncNowAll(): Promise<UseCaseResponse<SyncResult>> {
    await this.defineHeader();
    return axios
      .post(`${this.url}/api/canvas/CanvasCourseMapping/sync-now-all`)
      .then(resp => unwrap<SyncResult>(resp));
  }

  async GetSyncSettings(): Promise<UseCaseResponse<CanvasSyncSettings>> {
    await this.defineHeader();
    return axios
      .get(`${this.url}/api/canvas/CanvasCourseMapping/sync-settings`)
      .then(resp => unwrap<CanvasSyncSettings>(resp));
  }

  async UpdateSyncSettings(
    settings: CanvasSyncSettings
  ): Promise<UseCaseResponse<boolean>> {
    await this.defineHeader();
    return axios
      .put(`${this.url}/api/canvas/CanvasCourseMapping/sync-settings`, settings)
      .then(resp => unwrap<boolean>(resp));
  }
}
